import { inject, Injectable } from '@angular/core';
import { UserActionTypeAreaMap, UserActionTypeMap } from '@common/constants';
import { AuditODataItem } from '@common/models';
import { UserActionTypeAreaPipe, UserActionTypePipe } from '@common/pipes';
import { AuditService } from '@common/services';
import { AuditLogTag, AuditLogTagsMap, getUniqueListByProp } from '@common/services/smart-search';
import SmartSearchTemplatesBase from '@common/services/smart-search/smart-search-template-base';
import { FilterOptions } from '@common/types';
import { getFilterByContains, getFilterByEqFromEnum } from '@common/utils/functions/search';
import { ModelTemplate } from 'mbs-ui-kit';
import { asapScheduler, Observable, of, scheduled } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable()
export class SmartSearchModelTemplateAuditLogService extends SmartSearchTemplatesBase {
  private readonly auditService = inject(AuditService);
  private readonly auditLogService$ = this.auditService.getAuditLog();

  readonly #action = AuditLogTagsMap.get(AuditLogTag.Action);
  readonly #dateFrom = AuditLogTagsMap.get(AuditLogTag.DateFrom);
  readonly #dateTo = AuditLogTagsMap.get(AuditLogTag.DateTo);
  readonly #event = AuditLogTagsMap.get(AuditLogTag.Event);
  readonly #userName = AuditLogTagsMap.get(AuditLogTag.UserName);

  private keyCache = 'audit-log-name_';

  private readonly userActionTypePipe = new UserActionTypePipe();
  private readonly userActionTypeAreaPipe = new UserActionTypeAreaPipe();

  public readonly UserNameTag: ModelTemplate<AuditODataItem> = {
    tag: this.#userName.tag,
    items: (state): Observable<AuditODataItem[]> => {
      const term = state.leftCaretValue;
      const options: FilterOptions = {
        model: [{ value: term }],
        prop: this.#userName.prop
      };

      this.auditService.filter = term ? getFilterByContains(options) : '';

      // if (this.cache[this.keyCache + term]) {
      //   return of(this.cache[this.keyCache + term]) as Observable<AuditODataItem[]>;
      // }

      return scheduled(
        this.auditLogService$.pipe(
          map((res) => res.data),
          map((items) => {
            const filtered = items.filter((item) => item.UserName.toLowerCase().includes(term));

            return getUniqueListByProp(filtered, this.#userName.prop).slice(0, 10);
          }),
          tap<AuditODataItem[]>(this.writeToCache(this.keyCache + term))
        ),
        asapScheduler
      );
    },
    itemFormatter: ({ UserName }): string => UserName,
    addGroupBrackets: true
  };

  public readonly EventTag: ModelTemplate<AuditODataItem> = {
    tag: this.#event.tag,
    items: (state): Observable<AuditODataItem[]> => {
      const term = state.leftCaretValue;
      const options: FilterOptions = {
        model: [{ value: term }],
        prop: this.#event.prop,
        enumItems: UserActionTypeAreaMap
      };

      this.auditService.filter = term ? getFilterByEqFromEnum(options) : '';

      // if (this.cache[this.keyCache + term]) {
      //   return of(this.cache[this.keyCache + term]) as Observable<AuditODataItem[]>;
      // }

      return scheduled(
        this.auditLogService$.pipe(
          map((res) => res.data),
          map((items) => {
            const filtered = items.filter((item) => item.UserActionTypeArea.toLowerCase().includes(term));

            return getUniqueListByProp(filtered, this.#event.prop).slice(0, 10);
          }),
          tap<AuditODataItem[]>(this.writeToCache(this.keyCache + term))
        ),
        asapScheduler
      );
    },
    itemFormatter: ({ UserActionTypeArea }): string => this.userActionTypeAreaPipe.transform(UserActionTypeArea),
    addGroupBrackets: true
  };

  public readonly ActionTag: ModelTemplate<AuditODataItem> = {
    tag: this.#action.tag,
    items: (state): Observable<AuditODataItem[]> => {
      const term = state.leftCaretValue;
      const options: FilterOptions = {
        model: [{ value: term }],
        prop: this.#action.prop,
        enumItems: UserActionTypeMap
      };

      this.auditService.filter = term ? getFilterByEqFromEnum(options) : '';

      // if (this.cache[this.keyCache + term]) {
      //   return of(this.cache[this.keyCache + term]) as Observable<ExportResultsOdata[]>;
      // }

      return scheduled(
        this.auditLogService$.pipe(
          map((res) => res.data),
          map((items) => {
            const filtered = items.filter((item) => item.UserActionType.toLowerCase().includes(term));

            return getUniqueListByProp(filtered, this.#action.prop).slice(0, 10);
          }),
          tap<AuditODataItem[]>(this.writeToCache(this.keyCache + term))
        ),
        asapScheduler
      );
    },
    itemFormatter: ({ UserActionType }): string => this.userActionTypePipe.transform(UserActionType),
    addGroupBrackets: true
  };

  public readonly FromTag: ModelTemplate<AuditODataItem> = {
    tag: this.#dateFrom.tag,
    items: (): Observable<AuditODataItem[]> => of<AuditODataItem[]>([]),
    addGroupBrackets: true
  };

  public readonly ToTag: ModelTemplate<AuditODataItem> = {
    tag: this.#dateTo.tag,
    items: (): Observable<AuditODataItem[]> => of<AuditODataItem[]>([]),
    addGroupBrackets: true
  };
}
